import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getAdminUsers, deleteAdminUser, getAdminRoles } from "@/lib/api";
import { useAdminAccess } from "@/lib/adminAccess";
import { useAuth } from "@/contexts/AuthContext";
import { UserDialog } from "@/components/users/UserDialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { UserCog, Plus, Edit, Trash2, Shield, Mail, User, Eye } from "lucide-react";

const PRIMARY_ADMIN = "admin@primemediawireless.com";
const STATUS_BADGE = { on_leave: ["On leave", "bg-amber-100 text-amber-800"], inactive: ["Inactive", "bg-slate-200 text-slate-600"] };

const UserCard = ({ u, i, roleName, canEdit, isMe, onEdit, onSelf, onDelete }) => {
  const status = STATUS_BADGE[u.employment_status];
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
      <Card className="border-slate-200" data-testid={`user-card-${u.id}`}>
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${u.role === "admin" ? "bg-teal/10" : "bg-blue-50"}`}>
              {u.role === "admin" ? <Shield className="w-5 h-5 text-teal" /> : <User className="w-5 h-5 text-blue-500" />}
            </div>
            <div>
              <p className="font-heading font-semibold text-navy text-sm">{u.first_name} {u.last_name}</p>
              <div className="flex gap-1">
                {u.super_admin && <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800" data-testid={`super-admin-badge-${u.id}`}>Super Admin</span>}
                {status && <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${status[1]}`} data-testid={`user-status-badge-${u.id}`}>{status[0]}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
            <Mail className="w-4 h-4 text-slate-400" /><span className="font-body truncate">{u.email}</span>
          </div>
          {u.role === "staff" && !u.super_admin && (
            <p className="text-xs text-slate-500 mb-3" data-testid={`user-role-${u.id}`}>Security role: <b>{roleName(u.role_id) || "None"}</b></p>
          )}
          {canEdit ? (
            <div className="flex gap-2 pt-3 border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={onEdit} className="flex-1" data-testid={`edit-user-${u.id}`}><Edit className="w-4 h-4 mr-1" />Edit</Button>
              {u.role !== "admin" && u.email !== PRIMARY_ADMIN && !isMe && (
                <Button variant="outline" size="sm" onClick={onDelete} className="text-red-600 hover:bg-red-50" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
              )}
            </div>
          ) : isMe && (
            <div className="pt-3 border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={onSelf} className="w-full" data-testid={`view-my-info-${u.id}`}><Eye className="w-4 h-4 mr-1" />My information</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default function AdminUsers({ embedded = false }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [roles, setRoles] = useState([]);
  const { can, isAdmin } = useAdminAccess();
  const { user: me } = useAuth();
  const canManage = can("users", "manage");
  const meSuper = !!me?.super_admin;
  const canEdit = (u) => canManage && (u.super_admin ? meSuper : (isAdmin || u.role !== "admin"));

  const fetchUsers = () => getAdminUsers().then(setUsers).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { fetchUsers(); getAdminRoles().then(setRoles).catch(() => {}); }, []);
  const roleName = (id) => roles.find((r) => r.id === id)?.name;

  const handleDelete = async (userId) => {
    if (!window.confirm("Delete this user?")) return;
    try { await deleteAdminUser(userId); toast.success("User deleted"); fetchUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete user"); }
  };

  const content = (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500 font-body">{users.length} admin/staff users</p>
        {canManage && <Button onClick={() => setDialog({ mode: "create", target: null })} className="bg-teal hover:bg-teal-dark text-white" data-testid="create-user-btn">
          <Plus className="w-4 h-4 mr-2" />New User
        </Button>}
      </div>
      {loading ? <p className="text-slate-500 text-center py-8 font-body">Loading users...</p> : users.length === 0 ? (
        <div className="text-center py-12"><UserCog className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500 font-body">No users found</p></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((u, i) => (
            <UserCard key={u.id} u={u} i={i} roleName={roleName} canEdit={canEdit(u)} isMe={u.id === me?.id}
              onEdit={() => setDialog({ mode: "edit", target: u })} onSelf={() => setDialog({ mode: "self", target: u })}
              onDelete={() => handleDelete(u.id)} />
          ))}
        </div>
      )}
      <UserDialog open={!!dialog} onClose={() => setDialog(null)} target={dialog?.target} mode={dialog?.mode}
        roles={roles} meSuper={meSuper} onSaved={fetchUsers} />
    </div>
  );

  if (embedded) return content;
  return <AdminLayout title="User Management" breadcrumb="Users">{content}</AdminLayout>;
}
